import type {
  ColumnDef,
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { generateColumns, defaultHiddenColumns } from "../utils/columns";
import type { AdminRow, AdminSearchOptions, AdminFilterValue } from "../types";
import { parseSearchFilters } from "@/features/utils/search-filter-parser";
import { useEntityRows, useEntitySchema } from "./use-backoffice";

/**
 * Everything the generic grid needs for one table, from its name alone.
 *
 * Pagination, sorting, the parsed search box, the column filters and the
 * per-table column visibility live here; the page below only decides *which*
 * table and which implicit filters. The schema arrives asynchronously, so the
 * row query stays parked until it lands — otherwise the first request sorts by
 * a column the table may not have, and the server (rightly) rejects it.
 */

const storageKey = (table: string) => `backoffice-columns-${table}`;

/** Hidden columns remembered per table, tolerant of a wiped localStorage. */
const readStoredHidden = (table: string): string[] | null => {
  try {
    const raw = localStorage.getItem(storageKey(table));
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
};

const writeStoredHidden = (table: string, hidden: string[]) => {
  try {
    localStorage.setItem(storageKey(table), JSON.stringify(hidden));
  } catch {
    // A private window with storage denied is not a reason to break the table.
  }
};

export interface UseEntityTableOptions {
  table: string;
  /** Filters always applied, on top of whatever the user typed. */
  implicitFilters?: Record<string, AdminFilterValue>;
  /** Restrict to one tenant. */
  clientId?: string;
  defaultPageSize?: number;
  includeDeleted?: boolean;
  /** Columns merged in front of the generated ones (an actions menu, say). */
  extraColumns?: ColumnDef<AdminRow>[];
  /** Initial content of the search box (e.g. from a `?q=` link). */
  initialSearch?: string;
}

export function useEntityTable({
  table,
  implicitFilters,
  clientId,
  defaultPageSize = 50,
  includeDeleted = false,
  extraColumns,
  initialSearch = "",
}: UseEntityTableOptions) {
  const schemaQuery = useEntitySchema(table);
  const schema = schemaQuery.data;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  // The seed goes through the same parser as anything typed later, so a link
  // carrying `?q=status=failed` filters on arrival instead of sending the
  // literal string to the server as free text.
  const seed = useMemo(
    () => parseSearchFilters(initialSearch),
    [initialSearch],
  );
  const [search, setSearch] = useState(seed.search);
  const [searchFilters, setSearchFilters] = useState<
    Record<string, AdminFilterValue>
  >(seed.filters as Record<string, AdminFilterValue>);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Switching table resets everything: a page index, a sort column or a hidden
  // column from the previous entity means nothing on the next one.
  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
    setSorting([]);
    setSearch(seed.search);
    setSearchFilters(seed.filters as Record<string, AdminFilterValue>);
    setColumnVisibility({});
  }, [table]);

  // Column visibility: the stored preference if there is one, else the
  // entity's own "start hidden" list.
  useEffect(() => {
    if (!schema) return;
    const stored = readStoredHidden(schema.table);
    const hidden = new Set(stored ?? defaultHiddenColumns(schema));
    setColumnVisibility(
      Object.fromEntries(
        Object.keys(schema.columns).map((name) => [name, !hidden.has(name)]),
      ),
    );
  }, [schema]);

  const columns = useMemo(
    () => (schema ? generateColumns(schema, extraColumns) : []),
    [schema, extraColumns],
  );

  const sortBy = sorting[0]?.id || schema?.default_sort;
  const sortOrder: "asc" | "desc" = sorting[0]
    ? sorting[0].desc
      ? "desc"
      : "asc"
    : schema?.default_sort_order || "desc";

  const options: AdminSearchOptions = useMemo(
    () => ({
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
      search: search || undefined,
      client_id: clientId,
      include_deleted: includeDeleted || undefined,
      filters: { ...(implicitFilters || {}), ...searchFilters },
    }),
    [
      pagination.pageSize,
      pagination.pageIndex,
      sortBy,
      sortOrder,
      search,
      clientId,
      includeDeleted,
      // Structural equality: a caller rebuilding the object every render should
      // not refetch the table on every render.
      JSON.stringify(implicitFilters),
      JSON.stringify(searchFilters),
    ],
  );

  const rowsQuery = useEntityRows(table, options, !!schema);

  const handleColumnVisibility = useCallback(
    (
      updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => {
      setColumnVisibility((previous) => {
        const next =
          typeof updater === "function" ? updater(previous) : updater;
        if (schema) {
          writeStoredHidden(
            schema.table,
            Object.entries(next)
              .filter(([, visible]) => !visible)
              .map(([name]) => name),
          );
        }
        return next;
      });
    },
    [schema],
  );

  const handleGlobalFilter = useCallback(
    (nextSearch: string, filters?: Record<string, unknown>) => {
      setSearch(nextSearch);
      setSearchFilters((filters || {}) as Record<string, AdminFilterValue>);
      setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    },
    [],
  );

  const total = rowsQuery.data?.total ?? 0;

  return {
    schema,
    columns,
    data: rowsQuery.data?.data ?? [],
    total,
    pageCount: Math.ceil(total / pagination.pageSize) || 0,
    isLoading:
      schemaQuery.isLoading || (rowsQuery.isLoading && !rowsQuery.data),
    isFetching: rowsQuery.isFetching,
    error: (schemaQuery.error || rowsQuery.error) as Error | null,

    pagination,
    setPagination,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility: handleColumnVisibility,
    globalFilter: search,
    setGlobalFilter: handleGlobalFilter,

    /** The exact options the current page was fetched with — used by the export. */
    searchOptions: options,
    refetch: rowsQuery.refetch,
  };
}
